import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe, registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';
import { Svincolo, StatoSvincolo } from 'src/app/model/svincoli/Svincolo';
import { SvincoliService } from 'src/app/services/svincoli/svincoli.service';
import { AppConstants } from 'src/environments/constants';
import { CommonUtils } from 'src/utils/CommonUtils';
import { getBadgeStatus } from 'src/utils/StyleUtils';

registerLocaleData(localeIt, 'it-IT');

@Component({
  selector: 'app-dashboard-svincoli',
  templateUrl: './dashboard-svincoli.component.html',
  styleUrls: ['./dashboard-svincoli.component.scss'],
})
export class DashboardSvincoliComponent implements OnInit, OnDestroy {
  private langChangeSub!: Subscription;
  private svincoliCache: Svincolo[] = [];
  svincoliDownloadComplete = false;
  svincoliPresenti = false;
  serverError: boolean = false;
  erroreGestitoMex: String = null;
  _isTeamView: boolean = false;
  private datePipe = new DatePipe('it-IT');
  tableConfig;

  emptyImage: string =
    sessionStorage.getItem('theme') === 'dark'
      ? '../../../assets/images/empty-state-dark.svg'
      : '../../../assets/images/empty-state-light.svg';

  private readonly tableHeaderKeys = [
    { text: 'ordini.orderNumber', columnId: 'ordine', width: '20%' },
    { text: 'ordini.position', columnId: 'posizione', width: '20%' },
    { text: 'ordini.supplier', columnId: 'fornitore', width: '20%' },
    { text: 'svincoli.dataCambioStato', columnId: 'data', width: '20%' },
    { text: 'ordini.state', columnId: 'stato', width: '25%' },
  ];

  @Input() set isTeamView(value: boolean) {
    if (!CommonUtils.isNotNullOrUndefined(this._isTeamView)) {
      this._isTeamView = value;
    } else if (value !== this._isTeamView) {
      this._isTeamView = value;
      this.caricaSvincoli();
    }
  }

  constructor(
    private svincoliServ: SvincoliService,
    private router: Router,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.caricaSvincoli();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      if (this.svincoliCache.length > 0) {
        this.buildTableConfig();
      }
    });
  }

  ngOnDestroy() {
    this.langChangeSub?.unsubscribe();
  }

  private buildTableConfig(): void {
    const keys = this.tableHeaderKeys.map(h => h.text);
    const translations = this.translate.instant(keys);

    this.tableConfig = {
      tableWidthPercentage: true,
      hasInfoHeader: false,
      tableAriaLabel: this.translate.instant('Dashboard.svincoli'),
      tableHeader: {
        tableCellsHeader: this.tableHeaderKeys.map(h => ({
          text: translations[h.text] || h.text,
          columnId: h.columnId,
          columnWidth: h.width,
          textAlign: 'left',
        })),
        hiddenHeader: false,
        secondRightIconSticky: true,
      },
      tableRows: this.svincoliCache.map(svincolo =>
        this.mapSvincoloToTableRow(svincolo)
      ),
      tooltipZindex: 4,
    };
  }

  caricaSvincoli() {
    this.svincoliDownloadComplete = false;
    this.svincoliPresenti = false;
    this.erroreGestitoMex = null;
    this.serverError = false;
    this.svincoliServ
      .getSvincoli(
        this._isTeamView,
        AppConstants.DASHBOARD_FIRST,
        AppConstants.DASHBOARD_ORDINI_LENGHT
      )
      .subscribe(
        res => {
          if (res['errorMex'] != null) {
            this.erroreGestitoMex = res['errorMex'];
            console.log('Get svincoli error: ' + res['errorMex']);
          } else {
            let svincoli = res['respObj'];
            if (svincoli?.length > 0) {
              this.svincoliCache = svincoli;
              this.buildTableConfig();
              this.svincoliPresenti = true;
            } else {
              this.svincoliCache = [];
              this.svincoliPresenti = false;
            }
          }
          this.svincoliDownloadComplete = true;
        },
        err => {
          this.serverError = true;
          console.error('Error catch', err);
          this.svincoliDownloadComplete = true;
        }
      );
  }

  private mapSvincoloToTableRow(svincolo: Svincolo): any {
    const badge = getBadgeStatus(svincolo.stato);
    return {
      tableCells: [
        { type: 'text', text: svincolo.numeroOrdine || '-' },
        {
          type: 'text',
          text: svincolo.codicePosizione
            ? `Pos/${svincolo.codicePosizione}`
            : '-',
        },
        { type: 'text', text: svincolo.fornitore?.ragioneSociale || '-' },
        {
          type: 'text',
          text: svincolo.dataCambioStato
            ? this.datePipe.transform(svincolo.dataCambioStato, 'dd.MM.yyyy') ||
              '-'
            : '-',
        },
        {
          type: 'text-badge',
          text: svincolo.stato
            ? this.formatLabel(
                this.translate.instant(svincolo.stato.toString())
              )
            : '-',
          badgeStatus: badge.notificationStatus,
          badgeCustomColor: badge.backgroundColor,
        },
      ],
      secondRightIcon: 'icon-arrow-right',
      secondRightIconAriaLabel: 'Vai al dettaglio svincolo',
    };
  }
  formatLabel(segment) {
    return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
  }



  onSecondRightIconClick(event: any) {
    let rowIndex: number;
    if (
      typeof event?.detail?.row === 'string' &&
      event.detail.row.startsWith('row-')
    ) {
      rowIndex = Number.parseInt(event.detail.row.replace('row-', ''), 10);
    } else {
      rowIndex = event?.detail?.row;
    }

    const svincolo = this.svincoliCache[rowIndex];
    if (svincolo?.id) {
      this.router.navigate([AppConstants.SVINCOLI_ROUTE, svincolo.id]);
    }
  }
}
